import { Toggle } from "shared/components";
import { getText } from "shared/lib";
import {
	type GroundLevelAlterationTreePropertyDescriptor,
	type TreeDescriptor,
	TreePropertyType,
	updateTreeProperty,
} from "../lib";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";
import { useTreeDialogValidator } from "./lib";

export interface GroundLevelAlterationFieldProps {
	onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
	treeDescriptor: TreeDescriptor;
}

export function GroundLevelAlterationField<
	T extends GroundLevelAlterationTreePropertyDescriptor,
>({ onChange, treeDescriptor }: GroundLevelAlterationFieldProps) {
	const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
		{
			checker: (property) => !!property?.value,
			properties: treeDescriptor.properties,
			treePropertyType: TreePropertyType.ground_level_alteration,
		},
	);

	const groundLevelAlteration = treeDescriptor.properties.get(
		TreePropertyType.ground_level_alteration,
	) as GroundLevelAlterationTreePropertyDescriptor | undefined;

	const handleChange = (value: string) => {
		const newTreeDescriptor = updateTreeProperty<T>(
			treeDescriptor,
			TreePropertyType.ground_level_alteration,
			(draft) => {
				draft.value = value as T["value"];
			},
		);

		onChange?.(newTreeDescriptor);
	};

	return (
		<div className="flex flex-col gap-1">
			<FieldLabel isValid={isValid}>
				{getText(
					"Will the proposed works near this tree alter the ground level?",
				)}
			</FieldLabel>

			<div className="flex gap-2">
				<Toggle
					aria-invalid={!isValid && shouldShowValidation}
					className="aria-invalid:border-redBorder"
					onClick={() => {
						handleChange("yes");
					}}
					pressed={groundLevelAlteration?.value === "yes"}
					variant="rounded"
				>
					{getText("Yes")}
				</Toggle>
				<Toggle
					aria-invalid={!isValid && shouldShowValidation}
					className="aria-invalid:border-redBorder"
					onClick={() => {
						handleChange("no");
					}}
					pressed={groundLevelAlteration?.value === "no"}
					variant="rounded"
				>
					{getText("No")}
				</Toggle>
				<Toggle
					aria-invalid={!isValid && shouldShowValidation}
					className="aria-invalid:border-redBorder"
					onClick={() => {
						handleChange("unsure");
					}}
					pressed={groundLevelAlteration?.value === "unsure"}
					variant="rounded"
				>
					{getText("Unsure")}
				</Toggle>
			</div>

			<FieldValidationMessages messages={messages} />
		</div>
	);
}
